import type { ReactNode } from 'react';
import type { ChartTooltipComparison } from '../ChartTooltip/ChartTooltip';
import { seriesColor, type ChartLegendPlacement, type ChartSeries } from './chartTheme';

/**
 * Props every chart shares. Kept here rather than in one of the three chart
 * folders because none of them owns it — the same reasoning that puts
 * `chartTheme` alongside `ChartFrame`.
 */

/** A row of chart data. Values are read by the keys named in `series`. */
export type ChartRow = Record<string, string | number | null | undefined>;

export type ChartBaseProps = {
  /**
   * The comparison period's reading in the tooltip — "vs 1,100" and a change
   * badge after the value — given the hovered row and the series being read.
   *
   * The chart has no idea what the prior period was; the caller keeps it on the
   * row under a key of its own and formats it here. Returning `undefined`
   * (comparison off, no matching bucket) leaves the row as it always was.
   */
  formatCompare?: (row: ChartRow, seriesKey: string) => ChartTooltipComparison | undefined;
  /**
   * Plot height in px, excluding axis titles and legend. Defaults to Figma's
   * `Chart` slot, 224 since the 2026-09-16 update.
   *
   * `'fill'` takes the height of whatever slot the chart is in instead. Needed
   * by `Card`'s `State=2 charts` variant, where two plots share the chart area
   * and neither knows its own height — the card decides it, and a number here
   * would either overflow the slot or leave it short.
   */
  height?: number | 'fill';
  /** Where the legend sits. `none` when `Card`'s own `Legend` slot holds it. */
  legend?: ChartLegendPlacement;
  /** Show the hover readout. */
  tooltip?: boolean;
  /**
   * Opens the detail behind one plotted point — the conversations a slice, a
   * bar or a date stands on. The index is into the `data` the caller passed, so
   * resolving it back to a row is the caller's own array lookup.
   *
   * Providing it is what turns the readout from a label into a surface: it gains
   * an action, and with it the dwell-to-lock behaviour that makes the action
   * reachable. Left off, the readout is still anchored — that is a fix, not a
   * feature — but never interactive. See `useAnchoredTooltip`.
   *
   * **Whole point, not per series.** Clicking 08 Sep on an eleven-series plot
   * asks about 08 Sep, not about one bank on 08 Sep (user's call, 2026-09-24,
   * matching the product being rebuilt, where the same drill-down returns 878
   * conversations across every brand).
   */
  onViewDetails?: (index: number) => void;
  /** What the readout's action reads. */
  viewDetailsLabel?: ReactNode;
  /** Replace the plot with a loading message. */
  loading?: boolean;
  /**
   * Force the empty state. Left undefined, a chart works it out from the data —
   * no rows, or nothing but blanks across every series.
   */
  empty?: boolean;
  /** Shown in the empty state. */
  emptyLabel?: ReactNode;
  /**
   * Formats values on the Y axis and in the tooltip — thousands separators,
   * currency, percentages. One function so the two can never disagree.
   */
  formatValue?: (value: number) => string;
  /**
   * Formats axis ticks, where that should differ from the tooltip's rendering.
   * Defaults to `formatValue`.
   *
   * Exists because precision that belongs on a reading does not belong on a
   * gridline: a metric formatted to a tenth labels its axis "-10.0, -5.0, 0.0",
   * where the tick only ever lands on a round number and the decimal is noise.
   */
  formatAxisValue?: (value: number) => string;
  /** Figma `_X-axis label`. */
  xAxisTitle?: string;
  /** Figma `_Y-axis label`. */
  yAxisTitle?: string;
  /** Accessible name. Derived from the series when omitted. Not in Figma. */
  label?: string;
  className?: string;
};

/**
 * Line and bar: rows plotted against a category axis.
 *
 * `legend` is narrowed to exclude `right`: a vertical legend belongs to the
 * donut, which is round and leaves the space for one. A cartesian plot fills its
 * width, so its key goes underneath. Enforced in the type rather than left to
 * discipline — the same reasoning as the stylelint rules.
 */
export type CartesianChartProps = Omit<ChartBaseProps, 'legend'> & {
  data: ChartRow[];
  /**
   * Pins the value axis to `[min, max]` instead of letting Recharts pick from
   * the data. Set it where the metric's range is a fact rather than an
   * observation — a score bounded to +-10 should draw that scale even on a week
   * when it never left +-2, or the same shape means something different in
   * every period.
   */
  yDomain?: [number, number];
  /**
   * The exact value-axis ticks, where the automatic ones land badly.
   *
   * Recharts picks "nice" ticks from the domain and `tickCount`, which on a
   * pinned `[-10, 10]` at the designed six gridlines chooses a step of 8 and
   * labels -6, +2, +10 — skipping both zero and the bound, the only two numbers
   * on a signed scale a reader actually needs.
   */
  yTicks?: number[];
  /**
   * Width of the value-axis gutter. Defaults to Figma's 48 — a 40px
   * right-aligned number plus `space/8`. `'auto'` fits it to this chart's own
   * labels instead; see `yAxisProps`.
   */
  yAxisWidth?: number | 'auto';
  /**
   * Ceiling on how many labels a dense time axis draws. Defaults to the
   * product's ten, which is right at the 586 and 1192 card widths; a chart in a
   * narrower column has to ask for fewer or its labels overlap. See
   * `TARGET_TICKS`, and `CardSpan` for the caller that knows the width.
   */
  xTicksTarget?: number;
  /**
   * A second, quieter figure in the tooltip, beside the plotted reading — given
   * the hovered row and the series being read.
   *
   * For a plot whose value is a *presentation* of something else: a line of
   * shares knows the percentage and not the count it came from, and a reader
   * asked to act on "Robi 27.3%" wants to know whether that is 27 mentions or
   * 2,700. `shareOfTotal` keeps the counts on the row for exactly this, under
   * `countKey`.
   *
   * Returning `undefined` prints nothing, so a row without a companion figure
   * reads as it always did.
   */
  formatDetail?: (row: ChartRow, seriesKey: string) => string | undefined;
  /** Key holding each row's x-axis category. */
  xKey: string;
  /** One entry per plotted series, in palette order. */
  series: ChartSeries[];
  /** Where the legend sits. `none` when `Card`'s own `Legend` slot holds it. */
  legend?: Exclude<ChartLegendPlacement, 'right'>;
};

/**
 * A chart is empty when it has no rows, or when every series is blank across
 * every row. The second case matters: an API that returns rows of nulls would
 * otherwise draw an axis around nothing and read as a rendering bug.
 */
export function isEmpty(data: ChartRow[], series: ChartSeries[]): boolean {
  if (data.length === 0) return true;
  return !data.some((row) =>
    series.some((s) => {
      const v = row[s.key];
      return v !== null && v !== undefined && v !== '';
    }),
  );
}

/**
 * The donut's own emptiness: no slices, or slices that total nothing.
 *
 * Separate from `isEmpty` because a donut reads zeros differently. `isEmpty`
 * treats `0` as a reading — on a line chart it is one, and a filter that
 * emptied a Tuesday has to draw it — but a ring of zero-width slices is not a
 * chart, it is a blank circle.
 *
 * Exported rather than kept inside `DonutChart` so a `Card` deciding whether to
 * draw `State=Empty` asks the same question the donut inside it would, instead
 * of a similar one that agrees until it does not.
 */
export function isEmptyTotal(data: ChartRow[], valueKey: string): boolean {
  if (data.length === 0) return true;
  const total = data.reduce((sum, row) => {
    const value = Number(row[valueKey] ?? 0);
    return sum + (Number.isFinite(value) ? value : 0);
  }, 0);
  return total === 0;
}

/**
 * Not in Figma. A chart is an opaque image to assistive tech, so it gets a name
 * built from what it plots. This is a floor, not a substitute for the numbers in
 * text form — see plan.md §7.
 */
export function chartAriaLabel(kind: string, series: ChartSeries[], by?: string): string {
  const labels = series.map((s) => s.label);
  const joined =
    labels.length <= 1
      ? (labels[0] ?? '')
      : `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`;
  const subject = joined === '' ? kind : `${kind}: ${joined}`;
  return by ? `${subject} by ${by}` : subject;
}

/**
 * The tooltip payload for one row of a cartesian chart.
 *
 * Recharts used to build this — it handed `ChartTooltip` an entry per series at
 * the hovered point, and the component only ever read it. Since the readout is
 * anchored rather than pointer-tracked (`useAnchoredTooltip`), Recharts no
 * longer renders it, and the entry list has to be assembled from the same three
 * things Recharts assembled it from: the row, the series, and the ramp.
 *
 * **Every series is listed, including one with no value at this point.** That is
 * what Recharts did, and a readout that quietly drops a series at the points
 * where it happens to be blank is a readout whose rows move under the reader.
 *
 * Shared by line, area and bar, which differ in how they *draw* a row and not at
 * all in how they report one. The donut and the scatter build their own: a
 * slice is one entry rather than a row of series, and a scatter point carries
 * two axes' worth of reading.
 */
export function seriesPayload(row: ChartRow | undefined, series: ChartSeries[]) {
  if (!row) return [];
  return series.map((s, i) => ({
    name: s.label,
    value: row[s.key] ?? undefined,
    color: seriesColor(i, s.color),
    dataKey: s.key,
    payload: row,
  }));
}
